import { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Field,
  Label,
  Switch,
} from "@headlessui/react";
import Loader from "./Loader";
import { XMarkIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import { callFunction } from "../api/callFunction";
import EventAPI from "../api/EventApi";
import EventAttendanceApi from "../api/EventAttendanceApi";
import { GlobalContext } from "../context/GlobalContext";
import { showToast } from "../utils/showToast";
import { LOCATION_NOT_VERIFIED, checkInGateDetail, isCheckInUnverified } from "../utils/checkInGate";
import { useNavigate } from "react-router-dom";
import { MAX_CO_HOSTS, coHostActionFor, isCoHost, isPrimaryHost } from "../Events/utils/eventHosts";
import { answeredAtText, attendanceDetailText, attendanceState, reliabilityText } from "../Events/utils/attendance";
import AttendanceBadge from "./AttendanceBadge";

const CO_HOST_BUTTON_LABELS = { make: "Make co-host", remove: "Remove co-host", leave: "Stop co-hosting" };

export default function ImageModal({
  selectedUser,
  setOpen,
  onRemoveFromQueue,
  eventData,
  onRemove,
  // Only the guest list passes this (GIS-222): co-hosts are picked from it.
  showCoHostControls = false,
}) {
  const { globalState } = useContext(GlobalContext);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [extendTimeEnabled, setExtendTimeEnabled] = useState(false);
  const [isPremiumEnabled, setIsPremiumEnabled] = useState(false);
  const [coHostBusy, setCoHostBusy] = useState(false);
  const [reliability, setReliability] = useState(null);
  const { name, photoURL: avatarUrl, userID } = selectedUser || {};

  // How often this guest shows up after saying yes, across events. A record
  // about a person, so the rules keep it to admin operators; hosts never ask.
  const isSuperAdmin = !!globalState?.isSuperAdmin;
  useEffect(() => {
    setReliability(null);
    if (!isSuperAdmin || !userID) return undefined;
    let cancelled = false;
    EventAttendanceApi.getMemberReliability(userID).then((data) => {
      if (!cancelled) setReliability(data);
    });
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, userID]);

  const coHostAction = showCoHostControls && eventData
    ? coHostActionFor(eventData, userID, { uid: globalState?.vendorID, isSuperAdmin: globalState?.isSuperAdmin })
    : null;
  const hostRoleLabel = showCoHostControls && eventData
    ? (isPrimaryHost(eventData, userID) ? "Host" : isCoHost(eventData, userID) ? "Co-host" : null)
    : null;

  const handleCoHostAction = async () => {
    if (!eventData?.id || !userID || !CO_HOST_BUTTON_LABELS[coHostAction]) return;
    const who = name || "This guest";
    const question = {
      make: `Make ${who} a co-host? They'll be able to edit this event, manage guests and run check-in.`,
      remove: `Remove ${who} as a co-host?`,
      leave: "Stop co-hosting this event? You'll lose access to it.",
    }[coHostAction];
    if (!window.confirm(question)) return;
    setCoHostBusy(true);
    try {
      if (coHostAction === "make") {
        await EventAPI.addEventCoHost(eventData.id, userID);
        showToast(`${who} is now a co-host.`, { type: "success" });
        setOpen(false);
        return;
      }
      await EventAPI.removeEventCoHost(eventData.id, userID);
      setOpen(false);
      if (coHostAction === "leave") {
        showToast("You're no longer co-hosting this event.", { type: "success" });
        navigate("/events");
        return;
      }
      showToast(`${who} is no longer a co-host.`, { type: "success" });
    } catch (error) {
      showToast(error?.message || "Couldn't update co-hosts.", { type: "error" });
      setCoHostBusy(false);
    }
  };

  useEffect(() => {
    if (eventData && userID && eventData.eventUsers && eventData.eventUsers[userID]) {
      setExtendTimeEnabled(eventData.eventUsers[userID].allowExtendTime || false);
    }
  }, [eventData, userID]);

  useEffect(() => {
    if (eventData && userID && eventData.eventUsers && eventData.eventUsers[userID]) {
      setIsPremiumEnabled(eventData.eventUsers[userID].isPremium || false);
    }
  }, [eventData, userID]);

  useEffect(() => {
    if (!avatarUrl) return;
    const img = new Image();
    img.src = avatarUrl;
    img.onload = () => setIsLoading(false);
  }, [avatarUrl]);

  const [aspectRatio, setAspectRatio] = useState(null);

  useEffect(() => {
    if (!avatarUrl) return;

    const img = new Image();
    img.src = avatarUrl;
    img.onload = () => {
      setAspectRatio(img.width / img.height);
    };
  }, [avatarUrl]);

  const isVertical = aspectRatio !== null && aspectRatio < 0.8;

  const handleApprove = async () => {
    if (eventData && userID) {
      try {
        const updateObject = {
          [`eventUsers.${userID}.approved`]: true,
        };
        await EventAPI.updateEvent(eventData.id, updateObject);
        setOpen(false);
      } catch (error) {
        console.error("Error approving user:", error);
      }
    }
  };

  return (
    <Dialog
      open={true}
      onClose={() => setOpen(false)}
      className="relative z-[60]"
    >
      <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

      <div className="fixed inset-0 z-[60] flex items-center justify-center w-dvw h-4/5 top-20">
        <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 h-full w-full max-w-96">
          <button
            className="absolute top-0 right-0 m-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            onClick={() => setOpen(false)}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
          <div className="h-full mt-3 sm:mt-5">
            <div className="h-full text-center  flex flex-col py-4">
              <DialogTitle
                as="h3"
                className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-100"
              >
                {name}
              </DialogTitle>
              {hostRoleLabel && (
                <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-primary">
                  {hostRoleLabel}
                </p>
              )}
              {isCheckInUnverified(selectedUser) && (
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                  {LOCATION_NOT_VERIFIED}. {checkInGateDetail(selectedUser)}
                </p>
              )}
              {/* Their answer to "Can you make it?": what they wrote word for word
                  (nothing, if they tapped a button), when, and how it stands. */}
              {attendanceState(selectedUser?.attendance) && (
                <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                  <p>
                    <AttendanceBadge entry={selectedUser.attendance} className="mr-1 align-middle" />
                    {selectedUser.attendance.reply && (
                      <span className="italic">"{selectedUser.attendance.reply}"</span>
                    )}
                    {answeredAtText(selectedUser.attendance) && (
                      <span className="text-gray-400 dark:text-gray-500"> {answeredAtText(selectedUser.attendance)}</span>
                    )}
                  </p>
                  <p className="mt-0.5">{attendanceDetailText(selectedUser.attendance)}</p>
                </div>
              )}
              {reliabilityText(reliability) && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  At past events: {reliabilityText(reliability)}
                </p>
              )}
              <div className="my-2 flex-1 relative rounded-xl overflow-hidden">
                {isLoading || aspectRatio === null ? (
                  <div>
                    <Loader />
                  </div>
                ) : isVertical ? (
                  <img
                    src={avatarUrl}
                    alt="avatar"
                    className="h-full w-full object-cover" //object cover for vertical ratio photos
                  />
                ) : (
                  <>
                    {/* blurred bg photo */}
                    <img
                      src={avatarUrl}
                      alt="avatar background"
                      className="absolute inset-0 h-full w-full object-cover opacity-80"
                    />

                    {/* foreground bg object-contain for horizontal ratio photos */}
                    <div className="absolute inset-0 flex items-center justify-center backdrop-blur-xl rounded-xl">
                      <img
                        src={avatarUrl}
                        alt="avatar"
                        className="h-full w-full object-contain"
                      />
                    </div>
                  </>
                )}
              </div>
              {coHostAction === "full" && (
                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                  This event already has {MAX_CO_HOSTS} co-hosts.
                </p>
              )}
              {CO_HOST_BUTTON_LABELS[coHostAction] && (
                <button
                  type="button"
                  onClick={handleCoHostAction}
                  disabled={coHostBusy}
                  className="w-full rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  {coHostBusy ? "Saving..." : CO_HOST_BUTTON_LABELS[coHostAction]}
                </button>
              )}
              {
                onRemoveFromQueue &&
                <button
                  onClick={() => {
                    onRemoveFromQueue(userID);
                    setOpen(false);
                  }}
                  className="w-full rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                >
                  Remove from Queue
                </button>
              }
              {
                onRemove &&
                <button
                  onClick={() => {
                    onRemove(userID, selectedUser.type);
                    setOpen(false);
                  }}
                  className="w-full rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 mt-2"
                >
                  Remove from RSVP
                </button>
              }
              {
                eventData?.isPrivate && !eventData.eventUsers[userID]?.approved && (
                  <button
                    onClick={handleApprove}
                    className="w-full rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 mt-2"
                  >
                    Approve
                  </button>
                )
              }

              {
                eventData && globalState.isSuperAdmin && (
                  <Disclosure defaultOpen={false}>
                    <DisclosureButton className="group flex items-center justify-between w-full py-2 px-2 text-sm font-medium text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md">
                      Advanced Settings
                      <ChevronDownIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 group-data-[open]:rotate-180 transition-transform" />
                    </DisclosureButton>
                    <DisclosurePanel className="mt-1">
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-1"> {userID}</p>
                      <div className="flex items-center justify-between p-2">
                        <Field className="flex items-center">
                          <Label as="span" className="mr-2 text-sm">
                            <span className="text-gray-500 dark:text-gray-400 text-sm">Extend Time</span>
                          </Label>
                          <Switch
                            checked={extendTimeEnabled}
                            onChange={async (checked) => {
                              setExtendTimeEnabled(checked);
                              if (eventData && userID) {
                                try {
                                  const updateObject = {
                                    [`eventUsers.${userID}.allowExtendTime`]: checked,
                                  };
                                  await EventAPI.updateEvent(eventData.id, updateObject);
                                } catch (error) {
                                  console.error("Error updating extend time:", error);
                                  // Optionally revert the switch state if the update fails
                                  setExtendTimeEnabled(!checked);
                                }
                              }
                            }}
                            className="group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 dark:bg-gray-700 transition-colors duration-200 ease-in-out focus:outline-none data-[checked]:bg-primary dark:data-[checked]:bg-primary"
                          >
                            <span
                              aria-hidden="true"
                              className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
                            />
                          </Switch>
                        </Field>
                        <Field className="flex items-center">
                          <Label as="span" className="mr-2 text-sm">
                            <span className="text-gray-500 dark:text-gray-400 text-sm">Is Premium</span>
                          </Label>
                          <Switch
                            checked={isPremiumEnabled}
                            onChange={async (checked) => {
                              setIsPremiumEnabled(checked);
                              if (eventData && userID) {
                                try {
                                  const updateObject = {
                                    [`eventUsers.${userID}.isPremium`]: checked,
                                  };
                                  await EventAPI.updateEvent(eventData.id, updateObject);

                                  if (checked) {
                                    try {
                                      await callFunction("notifyPremiumGranted", { eventID: eventData.id, userID });
                                      showToast("Premium granted — user notified", { type: "success" });
                                    } catch (notifyError) {
                                      console.error("Error notifying user of premium grant:", notifyError);
                                      showToast("Premium granted, but notification failed", { type: "warning" });
                                    }
                                  }
                                } catch (error) {
                                  console.error("Error updating is premium:", error);
                                  // Optionally revert the switch state if the update fails
                                  setIsPremiumEnabled(!checked);
                                }
                              }
                            }}
                            className="group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 dark:bg-gray-700 transition-colors duration-200 ease-in-out focus:outline-none data-[checked]:bg-primary dark:data-[checked]:bg-primary"
                          >
                            <span
                              aria-hidden="true"
                              className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
                            />
                          </Switch>
                        </Field>
                      </div>
                    </DisclosurePanel>
                  </Disclosure>
                )
              }
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
